import { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { ToggleButton } from 'primereact/togglebutton';

export const RiesgoChart = ({ datos, maximo = 100, titulo = 'Gráfico de Riesgo', tooltipCallback, graficoRef }) => {
    const [chartType, setChartType] = useState('line');
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});

    const niveles = [
        { nombre: 'Muy Bajo (Nivel 1)', color: '#00b0f0', umbral: 0.2 },
        { nombre: 'Bajo (Nivel 2)', color: '#00ff00', umbral: 0.4 },
        { nombre: 'Medio (Nivel 3)', color: '#ffff00', umbral: 0.6 },
        { nombre: 'Alto (Nivel 4)', color: '#ffc000', umbral: 0.8 },
        { nombre: 'Muy Alto (Nivel 5)', color: '#ff0000', umbral: 1 }
    ];

    const hexToRgba = (hex, alpha = 1) => {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const getColorForValue = (valor, alpha = 1) => {
        const ratio = Math.min(valor / maximo, 1);
        for (let nivel of niveles) {
            if (ratio <= nivel.umbral) return hexToRgba(nivel.color, alpha);
        }
        return hexToRgba(niveles[niveles.length - 1].color, alpha);
    };

    const backgroundPlugin = {
        id: 'backgroundPlugin',
        beforeDatasetsDraw(chart) {
            const { ctx, chartArea, scales } = chart;
            if (!chartArea) return;
            if (!scales || !scales.y) return;
            const { left, right } = chartArea;
            const y = scales.y;
            for (let i = 0; i < niveles.length; i++) {
                const fromValue = i === 0 ? 0 : niveles[i - 1].umbral * maximo;
                const toValue = niveles[i].umbral * maximo;
                if (typeof y.getPixelForValue !== 'function') continue;
                const yFrom = y.getPixelForValue(fromValue);
                const yTo = y.getPixelForValue(toValue);
                // Color del fondo
                ctx.save();
                const isLineChart = chartType === 'line';
                if (isLineChart) {
                    ctx.fillStyle = hexToRgba(niveles[i].color, 0.02);
                } else {
                    ctx.fillStyle = hexToRgba(niveles[i].color, 0.15);
                }
                ctx.fillRect(left, yTo, right - left, yFrom - yTo);
                ctx.restore();
                // Texto
                const yMiddle = (yFrom + yTo) / 2;
                const xCenter = (left + right) / 2;
                ctx.save();
                ctx.fillStyle = 'rgba(0,0,0,0.8)';
                ctx.font = 'bold 15px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(niveles[i].nombre, xCenter, yMiddle);
                ctx.restore();
            }
        }
    };

    const createSegmentedGradient = (canvas, chartArea, valores) => {
        const gradient = canvas.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
        const step = 1 / (valores.length - 1);
        for (let i = 0; i < valores.length - 1; i++) {
            const color1 = getColorForValue(valores[i], 1);
            const color2 = getColorForValue(valores[i + 1], 1);
            gradient.addColorStop(step * i, color1);
            gradient.addColorStop(step * (i + 1), color2);
        }
        return gradient;
    };

    useEffect(() => {
        if (!datos || datos.length === 0) return;
        const labels = datos[0].valores.map(d => d.sigla_riesgo); // Mantener todas iguales
        const isLineChart = chartType === 'line';
        const datasets = datos.map((dataset) => {
            const valores = dataset.valores.map(v => parseFloat(v.valor));
            const colores = valores.map(v => getColorForValue(v, 1));
            return {
                label: dataset.label,
                data: valores,
                backgroundColor: isLineChart ? 'rgba(0,0,0,0)' : colores,
                borderColor: isLineChart
                    ? (ctx) => {
                        const chart = ctx.chart;
                        const { ctx: canvas, chartArea } = chart;
                        if (!chartArea) return 'rgba(0,0,0,0)';
                        return createSegmentedGradient(canvas, chartArea, valores);
                    }
                    : colores,
                borderWidth: 2,
                pointBackgroundColor: colores,
                pointBorderColor: colores,
                fill: false,
                tension: 0.4
            };
        });
        setChartData({ labels, datasets });

        setChartOptions({
            responsive: true,
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                },
                title: {
                    display: true, text: titulo,
                    font: {
                        size: 16,
                    }
                },
                tooltip: {
                    callbacks: {
                        label: tooltipCallback
                            ? (context) => tooltipCallback(context)
                            : (context) => `${context.dataset.label}: ${context.parsed.y}`
                    }
                },
                backgroundPlugin: {},
            },
            scales: {
                x: {
                    grid: {
                        display: false,
                    },
                },
                y: {
                    beginAtZero: true,
                    max: maximo,
                    suggestedMax: 5.25,
                    ticks: {
                        stepSize: 0.5,
                    },
                    grid: {
                        color: (context) => {
                            const val = context.tick.value;
                            // si es entero, pinto negro; si no, transparente
                            return Number.isInteger(val) ? 'black' : 'rgba(0,0,0,0)';
                        }
                    }
                }
            }
        });
    }, [datos, chartType]);

    return (
        <div className="p-fluid">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <ToggleButton checked={chartType === 'line'} onChange={(e) => setChartType(e.value ? 'line' : 'bar')} onLabel="Línea" offLabel="Barras" onIcon="pi pi-chart-line" offIcon="pi pi-chart-bar" className="p-button-outlined" />
            </div>
            <div ref={graficoRef}>
                <Chart type={chartType} data={chartData} options={chartOptions} plugins={[backgroundPlugin]} />
            </div>
        </div>
    );
};